// The page's own rescue when the app never starts: index.html arrived (from the server or the service worker's
// cache) but the app's script or styles didn't, which on a phone over USB happens whenever the forwards drop. It is
// bundled and inlined into index.html by vite.boot.ts, so it runs when every other file fails, and it shows the
// fallback already inside #root, which React replaces when it mounts. It depends on nothing else in the app.

/** Set on window by main.tsx once React has the page. Until then this script may retry. */
export const MOUNTED_FLAG = "__whistleMounted";
export const FALLBACK_ID = "boot-fallback";
/** sessionStorage key: when this tab last reloaded by itself. Survives the reloads it counts. */
export const RELOADS_KEY = "whistle.bootReloads";
/** At most this many automatic reloads per tab in RELOAD_WINDOW_MS; after that only the Retry button reloads. */
export const MAX_AUTO_RELOADS = 3;
export const RELOAD_WINDOW_MS = 2 * 60_000;

export const SAY_SLOW = "It's taking longer than it should. Tap Retry if it doesn't open.";
export const SAY_GAVE_UP = "It still didn't open after a few tries. Tap Retry to try again.";

type Store = Pick<Storage, "getItem" | "setItem">;

export type BootFallbackEnv = {
  win: Pick<Window, "addEventListener"> & { location: { origin: string; href: string } };
  doc: Pick<Document, "addEventListener" | "getElementById" | "querySelector" | "visibilityState">;
  /** Reload the page. Defaults to location.reload(). */
  reload?: () => void;
  /** Whether a file can be fetched now. Defaults to fileAnswers. */
  probe?: (url: string) => Promise<boolean>;
  /** Where automatic reloads are counted. Defaults to sessionStorage; null (or a throwing store) turns them off. */
  storage?: Store | null;
  now?: () => number;
  retryEveryMs?: number;
  /** After the load event, how long the app has to mount before the boot counts as failed. */
  settleMs?: number;
  /** With no load event and no error at all (a request that hangs), when to give up waiting. */
  stuckMs?: number;
};

export type BootFallback = { readonly failed: boolean; fail: () => void; retry: () => Promise<void> };

/**
 * Whether a file can be fetched now. Scripts and stylesheets must come back as such: an SPA server (vite preview among
 * them) answers a missing file with index.html and a 200. A request that hangs counts as no answer.
 */
export async function fileAnswers(url: string, fetchImpl: typeof fetch = fetch, timeoutMs = 5000): Promise<boolean> {
  const abort = typeof AbortController === "function" ? new AbortController() : null;
  const timer = abort ? setTimeout(() => abort.abort(), timeoutMs) : undefined;
  try {
    const res = await fetchImpl(url, { cache: "no-store", credentials: "same-origin", signal: abort?.signal });
    if (!res.ok) return false;
    const type = res.headers.get("content-type") ?? "";
    const path = url.split(/[?#]/)[0] ?? "";
    if (/\.m?js$/i.test(path)) return /javascript/i.test(type);
    if (/\.css$/i.test(path)) return /text\/css/i.test(type);
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function defaultStorage(): Store | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function installBootFallback(env: BootFallbackEnv): BootFallback {
  const { win, doc } = env;
  const mounted = () => !!(win as unknown as Record<string, unknown>)[MOUNTED_FLAG];
  const reload = env.reload ?? (() => (win as unknown as Window).location.reload());
  const probe = env.probe ?? ((url: string) => fileAnswers(url));
  const storage = env.storage === undefined ? defaultStorage() : env.storage;
  const now = env.now ?? (() => Date.now());
  let failed = false;
  let reloading = false;
  let probing = false;
  let gaveUp = false;
  // Whether the server was seen not answering since the fallback appeared. Only a server that was down and came back
  // is a reason to reload by itself: if the files answer and the app still didn't start (a hung third-party stylesheet,
  // an exception at start-up, or the worker's cache answering the probe), a reload would only repeat it, forever.
  let seenDown = false;
  const failedUrls = new Set<string>();
  let timer: ReturnType<typeof setInterval> | undefined;

  const entryUrl = () => (doc.querySelector('script[type="module"][src]') as HTMLScriptElement | null)?.src || win.location.href;

  const say = (text: string) => {
    const p = doc.getElementById(FALLBACK_ID)?.querySelector("p");
    if (p) p.textContent = text;
  };

  const show = () => {
    const el = doc.getElementById(FALLBACK_ID);
    if (!el) {
      doc.addEventListener("DOMContentLoaded", show, { once: true });
      return;
    }
    el.hidden = false;
    el.querySelector("button")?.addEventListener("click", () => {
      reloading = true;
      reload();
    });
  };

  /** Records an automatic reload if this tab hasn't used up its allowance. No storage, no automatic reloads. */
  const takeReload = (): boolean => {
    if (!storage) return false;
    try {
      const t = now();
      const raw: unknown = JSON.parse(storage.getItem(RELOADS_KEY) ?? "[]");
      const recent = (Array.isArray(raw) ? raw : []).filter((x): x is number => typeof x === "number" && t - x < RELOAD_WINDOW_MS);
      if (recent.length >= MAX_AUTO_RELOADS) return false;
      storage.setItem(RELOADS_KEY, JSON.stringify([...recent, t]));
      return true;
    } catch {
      return false;
    }
  };

  const stop = () => clearInterval(timer);

  // Probes what failed (or the app's script), and reloads only once it answers after having been seen down, so a
  // server that is still down never swaps this page for the browser's error page.
  const retry = async () => {
    if (mounted()) {
      stop();
      return;
    }
    if (reloading || probing || gaveUp) return;
    probing = true;
    const targets = failedUrls.size ? [...failedUrls] : [entryUrl()];
    const ok = (await Promise.all(targets.map((u) => probe(u)))).every(Boolean);
    probing = false;
    if (mounted() || reloading) return;
    if (!ok) {
      seenDown = true;
      return;
    }
    if (!seenDown) {
      say(SAY_SLOW);
      return;
    }
    if (!takeReload()) {
      gaveUp = true;
      stop();
      say(SAY_GAVE_UP);
      return;
    }
    reloading = true;
    reload();
  };

  const fail = () => {
    if (failed || mounted()) return;
    failed = true;
    show();
    timer = setInterval(() => void retry(), env.retryEveryMs ?? 5000);
    win.addEventListener("online", () => void retry());
    doc.addEventListener("visibilitychange", () => {
      if (doc.visibilityState === "visible") void retry();
    });
  };

  // A script or stylesheet of our own that failed to load: the server didn't answer for it. These error events don't
  // bubble, so listen while capturing; errors thrown by scripts arrive at the window itself and are not load failures.
  win.addEventListener(
    "error",
    (ev) => {
      const el = ev.target as (Element & { src?: string; href?: string }) | null;
      if (!el || (el as unknown) === win || typeof el.tagName !== "string") return;
      const tag = el.tagName.toUpperCase();
      const url = tag === "SCRIPT" ? el.src : tag === "LINK" ? el.href : "";
      if (!url || !url.startsWith(win.location.origin) || mounted()) return;
      failedUrls.add(url);
      seenDown = true;
      fail();
    },
    true,
  );
  // These only show the fallback with its Retry button; on their own they never reload.
  win.addEventListener("load", () => {
    setTimeout(() => {
      if (!mounted()) fail();
    }, env.settleMs ?? 3000);
  });
  setTimeout(() => {
    if (!mounted()) fail();
  }, env.stuckMs ?? 15_000);

  return {
    get failed() {
      return failed;
    },
    fail,
    retry,
  };
}
